import React from 'react';

import axios from 'axios';

import { Answer, Question } from '~/types';

import StyledButton from '~/components/StyledButton';

import { AppContext } from '~/contexts/AppContext';
import { QuestionContext } from '~/contexts/QuestionContext';

import Fade from '~/components/Fade';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStreetView } from '@fortawesome/free-solid-svg-icons'

import { SpinnerCircularFixed } from 'spinners-react';
import * as topicUtils from '~/util/topic';
import MatomoTracker from '~/util/matomo/MatomoTracker';
import { useParams } from 'react-router-dom';

const LocationRequest: React.FC = () => {

    const { topicData, updatePreciseLocation, apiUrl, preciseCoordinates } = useContext(AppContext)!;
    const { ratingId, question, questionTime, onSubmitAnswer } = useContext(QuestionContext)!;

    const [state, setState] = useState({ loading: false });

    const params: { topicHash: string } = useParams();

    const { i18n, t } = useTranslation();

    const submitAnswerText = async (answerText: string, nextQuestionId: number | null | undefined) => {

        await axios.post(`${apiUrl}/rating-answer/${topicData?.topic.id}/location-request`, {
            ratingId: ratingId,
            questionnaireId: question.questionnaireId,
            questionId: question.questionId,
            preciseCoordinates: preciseCoordinates,
            timeToAnswerMs: !questionTime ? 0 : Date.now() - questionTime,
            lang: i18n.language,
            answerText: answerText
        });

        MatomoTracker.trackEvent({
            category: 'qna', 
            action: 'submit-answer',
            documentTitle: topicData?.topic.name,
            href: `https://kazva.bg/${params.topicHash}`,
            customDimensions: [
                { id: 3, value: question.questionText },
                { id: 4, value: answerText },
                { id: 5, value: '' }
            ]
        });

        onSubmitAnswer(nextQuestionId ?? null);
    };

    const buttonClicked = async (answer: Answer) => {

        if ("geolocation" in navigator) {

            setState({ ...state, loading: true });

            try {
                await updatePreciseLocation();
                setState({ ...state, loading: false });
                submitAnswerText('location request granted', answer.nextQuestionId);
            } catch (error: unknown) {
                setState({ ...state, loading: false });
                // Error callback - user denied permission or another error occurred
                if(error instanceof GeolocationPositionError) {
                    const geolocationError = error as GeolocationPositionError;
                    switch (geolocationError.code) {
                        case geolocationError.PERMISSION_DENIED:
                            submitAnswerText('location request denied', answer.nextQuestionId);
                            break;
                        case geolocationError.POSITION_UNAVAILABLE:
                        case geolocationError.TIMEOUT:
                        default:
                            submitAnswerText('location request error', answer.nextQuestionId);
                            break;
                    }
                } else {
                    submitAnswerText('unknown error', answer.nextQuestionId);
                }
            }

        } else {
            console.log("Geolocation is not supported by this browser.");
        }
    };

    return (
        <Fade cascade>
            {question.answers.map((answer: Answer, i: number) =>
                <React.Fragment key={i}>
                    <StyledButton onClick={() => buttonClicked(answer)} disabled={state.loading}>
                        {state.loading ?
                            <>
                                <SpinnerCircularFixed
                                    speed={400}
                                    color={'#333'}
                                    secondaryColor={'#666'}
                                    thickness={100}
                                    size={24}
                                    style={{ verticalAlign: 'middle', margin: '-5px 2px 0 0' }}
                                /> Зареждане...
                            </> :
                            <>
                                <FontAwesomeIcon icon={faStreetView} /> {topicUtils.getAnswerText(
                                    topicData,
                                    question.questionId,
                                    answer.answerId,
                                    i18n.language
                                )}
                            </>
                        }
                    </StyledButton>
                    <StyledButton onClick={() => submitAnswerText('location request skipped', answer.nextQuestionId)} disabled={state.loading}>
                        {t('button_skip')}
                    </StyledButton>
                </React.Fragment>
            )}
        </Fade>
    );
};

export default LocationRequest;